import { useTranslation } from 'react-i18next';
import { useFormContext } from 'react-hook-form';
import { cn, PaletteUtils } from '@shared/utils';
import type { IForm } from './accountDetails';
import { Button, ErrorMessage, Icon, Text } from '@shared/widgets';
import CopyIcon from '@assets/icons/copy.svg';
import EditIcon from '@assets/icons/edit.svg';

interface ItemProps {
  name: string;
  value: string;
  isEditableLabel: boolean;
  isEditableCost: boolean;
  isCopy: boolean;
}

export const Item = (props: ItemProps) => {
  const {
    register,
    formState: { errors },
    setFocus,
  } = useFormContext<IForm>();
  const { t } = useTranslation('account');

  const copyHandler = async () => await navigator.clipboard.writeText(props.value);
  const editHandler = () => {
    switch (props.name) {
      case t('details.fields.accountLabel'):
        setFocus('accountLabel');
        break;
      case t('details.fields.costCentre'):
        setFocus('costCentre');
        break;
      default:
        break;
    }
  };

  const isEditing = props.isEditableLabel || props.isEditableCost;
  return (
    <div className='flex items-center justify-between gap-3'>
      <div>
        <Text size='sm' color='secondary'>
          {props.name}
        </Text>
        <Text className={cn('max-w-52 break-words', 'max-xl:max-w-none')} weight='semibold' size='sm' color='primary'>
          {isEditing ? (
            <>
              {props.isEditableLabel && (
                <>
                  <input
                    {...register('accountLabel', { required: t('errors.form.required', { ns: 'common' }) })}
                    className='w-full appearance-none rounded-sm border-none bg-transparent p-0 outline-none'
                    placeholder={`${t('placeholders.enter', { ns: 'common' })} ${props.name.toLowerCase()}... *`}
                  />
                  <ErrorMessage text={errors.accountLabel?.message} />
                </>
              )}
              {props.isEditableCost && (
                <>
                  <input
                    {...register('costCentre', { required: t('errors.form.required', { ns: 'common' }) })}
                    className='w-full appearance-none rounded-sm border-none bg-transparent p-0 outline-none'
                    placeholder={`${t('placeholders.enter', { ns: 'common' })} ${props.name.toLowerCase()}... *`}
                  />
                  <ErrorMessage text={errors.costCentre?.message} />
                </>
              )}
            </>
          ) : (
            props.value
          )}
        </Text>
      </div>
      <div className='flex items-center gap-2'>
        {props.isEditableLabel && (
          <Button color='icon' onClick={editHandler}>
            <Icon color={PaletteUtils.isInsigniaPalette ? 'primary' : undefined} icon={<EditIcon width='22' height='22' />} />
          </Button>
        )}
        {props.isEditableCost && (
          <Button color='icon' onClick={editHandler}>
            <Icon color={PaletteUtils.isInsigniaPalette ? 'primary' : undefined} icon={<EditIcon width='22' height='22' />} />
          </Button>
        )}
        {props.isCopy && (
          <Button color='icon' onClick={copyHandler}>
            <Icon color={PaletteUtils.isInsigniaPalette ? 'primary' : undefined} icon={<CopyIcon />} />
          </Button>
        )}
      </div>
    </div>
  );
};
